<template>
  <es-loader v-if="loading" style="margin: 4.5rem"/>
  <es-exception v-else-if="loadingError"
    exceptionMessage="There was an issue.
      Please try again or contact your administrator."
    margin="1rem"
    style="padding: 5rem"
  />
  <div v-else>
    <div
      class="document"
      :class="{ 'margin--top': isRetired }"
    >
      <div>
        <div class="header is-flex">
          <es-header>
            <template #header>
              <div class="header-title">
                History Record Details
                <span class="header-subtitle">
                  (Creation Date: {{ record.createdDate | formatDate }})
                </span>
              </div>
            </template>
          </es-header>
          <div class="icon-right">
            <router-link
              v-if="this.activeModule.name === 'personId'"
              :to="{
                name: 'PidHistoryRecords',
                query: {
                  retired: isRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="Go Back"
              />
            </router-link>
            <router-link
              v-if="this.activeModule.name === 'directMatch'"
              :to="{
                name: 'DmHistoryRecords',
                query: {
                  retired: isRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="Go Back"
              />
            </router-link>
          </div>
        </div>
        <div class="content">
          <div class="columns">
            <div class="column master-record--column">
              <es-form style="margin-right: 0rem;">
                <template #es-form-header>
                  <div
                    v-if="anyFieldsEnabled(personInformationFieldNames)"
                    class="header-container"
                  >
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'user']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Person Information
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div
                    v-if="fieldEnabled('FIRST_NAME')"
                    class="row top-row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('FIRST_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.firstName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('MIDDLE_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('MIDDLE_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.middleName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('LAST_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('LAST_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.lastName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('SUFFIX')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SUFFIX') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.suffix }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('PREVIOUS_LAST_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('PREVIOUS_LAST_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.previousLastName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('FULL_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('FULL_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.fullName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('AKA_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('AKA_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.akaName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('GENDER_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('GENDER_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mappedGender }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('DATE_OF_BIRTH')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('DATE_OF_BIRTH') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <span v-if="record.dateOfBirth">
                              {{
                                displayDOBFormat(record.dateOfBirth)
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('SOCIAL_SECURITY_NUMBER')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div
                          v-if="hasModule('PRSID:PRPRF:SSN:VIEW')"
                          class="field"
                        >
                          <div class="control field-label is-normal">
                            {{ record.socialSecurityNumber }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('ETHNICITY_INDICATOR')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ETHNICITY_INDICATOR') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mappedEthnicity }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RACE_ETHNIC_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RACE_ETHNIC_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapRace(record.raceEthnicCode) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RACE2_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RACE2_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapRace(record.race2Code) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RACE3_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RACE3_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapRace(record.race3Code) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RACE4_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RACE4_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapRace(record.race4Code) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RACE5_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RACE5_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapRace(record.race5Code) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('ETHNIC_SUB_GROUP')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ETHNIC_SUB_GROUP') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapEthnicSubGroup(record.ethnicSubGroup) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('STATE_STUDENT_ID')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('STATE_STUDENT_ID') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ formatStateID(record.personId) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
            </div>
            <div class="column master-record--column">
              <es-form>
                <template #es-form-header>
                  <div
                    v-if="anyFieldsEnabled(contactInformationFieldNames)"
                    class="header-container"
                  >
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'address-book']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Contact Information
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div
                    class="row top-row"
                    v-if="fieldEnabled('STREET_1')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('STREET_1') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.street1 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('STREET_2')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('STREET_2') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.street2 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('CITY')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('CITY') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.city }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('STATE_CODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('STATE_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mappedState }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('ZIP')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ZIP') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.zip }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('GEOCODE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('GEOCODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.geocode }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
              <es-form>
                <template #es-form-header>
                  <div
                    v-if="anyFieldsEnabled(credentialInformationFieldNames)"
                    class="header-container"
                  >
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'diploma']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Credential Information
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div
                    class="row top-row"
                    v-if="fieldEnabled('HIGHEST_DEGREE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('HIGHEST_DEGREE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapHighestDegreeEarned(record.highestDegree) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('SUBJECT_MATTER_AREA')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SUBJECT_MATTER_AREA') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapSubjectMatterArea(record.subjectMatterArea) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('DEGREE_PROVIDER')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('DEGREE_PROVIDER') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.degreeProvider }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('CERTIFICATE_NUMBER')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('CERTIFICATE_NUMBER') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.certificateNumber }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('CERTIFICATE_TYPE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('CERTIFICATE_TYPE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapCertificateType(record.certificateType) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
            </div>
            <div class="column master-record--column">
              <es-form>
                <template #es-form-header>
                  <div
                    v-if="anyFieldsEnabled(otherInformationFieldNames)"
                    class="header-container"
                  >
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'list-alt']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Other Information
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div
                    class="row top-row"
                    v-if="fieldEnabled('BIRTH_LOCATION')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('BIRTH_LOCATION') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.birthLocation }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RESIDENCY_STATUS')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RESIDENCY_STATUS') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ mapResidencyStatus(record.residencyStatus) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('RESIDENCY_EXPIRATION_DATE')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RESIDENCY_EXPIRATION_DATE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <span v-if="record.residencyExpirationDate">
                              {{ record.residencyExpirationDate | formatDateMMDDYYYY }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('MOTHERS_MAIDEN_NAME')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('MOTHERS_MAIDEN_NAME') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.mothersMaidenName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
              <es-form>
                <template #es-form-header>
                  <div
                    v-if="anyFieldsEnabled(cdfFieldNames)"
                    class="header-container"
                  >
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'database']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Customer Defined Fields
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div
                    class="row top-row"
                    v-if="fieldEnabled('cdf_1')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_1`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_1 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_2')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_2`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_2 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_3')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_3`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_3 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_4')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_4`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_4 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_5')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_5`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_5 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_6')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_6`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_6 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_7')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_7`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_7 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_8')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_8`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_8 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_9')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_9`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_9 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_10')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_10`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_10 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row"
                    v-if="fieldEnabled('cdf_11')"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{
                            getFieldLabel(
                              `CDF:${record.personType}:ASSIGN:cdf_11`
                            )
                          }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            {{ record.personCdf.cdf_11 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <person-profile-history-location-info
      :isRetired="isRetired"
      :person="record"
      :personLocations="recordLocationHistory"
      style="margin-top: 1rem"
    />
    <div
      v-if="!this.isRetired"
      class="info-columns"
    >
        <person-profile-history-matching-information
          :personMatchingInformation="personMatchingInformation"
        />
        <person-profile-history-transaction-information
          :personTransactionInformation="personTransactionInformation"
          :person="person"
        />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDoubleLeft } from '@fortawesome/pro-regular-svg-icons';

import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import PersonProfileHistoryLocationInfo from '@/views/PersonProfile/PersonProfileHistoryLocationInfo.vue';
import PersonProfileHistoryMatchingInformation from '@/views/PersonProfile/PersonProfileHistoryMatchingInformation.vue';
import PersonProfileHistoryTransactionInformation from '@/views/PersonProfile/PersonProfileHistoryTransactionInformation.vue';

import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import LookupsUtils from '@/mixins/LookupsUtils';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import StateIdUtils from '@/mixins/StateIdUtils';

library.add(faAngleDoubleLeft);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    LookupsUtils,
    NotificationManager,
    PermissionManager,
    StateIdUtils,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    PersonProfileHistoryLocationInfo,
    PersonProfileHistoryMatchingInformation,
    PersonProfileHistoryTransactionInformation,
  },
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      personMatchingInformation: [],
      personTransactionInformation: {},
      record: {},
      recordLocationHistory: [],
    };
  },
  created() {
    this.onLoad();
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
      'getFieldLabelByPersonType',
    ]),
    ...mapGetters('lookups', [
      'activeCertificateTypes',
      'activeEthnicities',
      'activeEthnicSubGroups',
      'activeGenders',
      'activeHighestDegrees',
      'activeRaces',
      'activeResidencyStatuses',
      'activeStates',
      'activeSubjectMatterAreas',
    ]),
    ...mapGetters('propertiesModule', ['properties']),
    cdfFieldNames() {
      return [
        'cdf_1',
        'cdf_10',
        'cdf_11',
        'cdf_2',
        'cdf_3',
        'cdf_4',
        'cdf_5',
        'cdf_6',
        'cdf_7',
        'cdf_8',
        'cdf_9',
      ];
    },
    contactInformationFieldNames() {
      return [
        'CITY',
        'GEOCODE',
        'STATE_CODE',
        'STREET_1',
        'STREET_2',
        'ZIP',
      ];
    },
    credentialInformationFieldNames() {
      return [
        'CERTIFICATE_NUMBER',
        'CERTIFICATE_TYPE',
        'DEGREE_PROVIDER',
        'HIGHEST_DEGREE',
        'SUBJECT_MATTER_AREA',
      ];
    },
    mappedEthnicity() {
      const code = this.record.ethnicityIndicator;
      return this.getDescriptionFromCode(code, this.activeEthnicities, code);
    },
    mappedGender() {
      const code = this.record.genderCode;
      return this.getDescriptionFromCode(code, this.activeGenders, code);
    },
    mappedState() {
      const item = this.activeStates.find(s => s.code === this.record.stateCode);
      return item ? item.description : '\u00A0';
    },
    otherInformationFieldNames() {
      return [
        'BIRTH_LOCATION',
        'MOTHERS_MAIDEN_NAME',
        'RESIDENCY_EXPIRATION_DATE',
        'RESIDENCY_STATUS',
      ];
    },
    personInformationFieldNames() {
      return [
        'AKA_NAME',
        'DATE_OF_BIRTH',
        'ETHNICITY_INDICATOR',
        'ETHNIC_SUB_GROUP',
        'FIRST_NAME',
        'FULL_NAME',
        'GENDER_CODE',
        'LAST_NAME',
        'MIDDLE_NAME',
        'PREVIOUS_LAST_NAME',
        'RACE2_CODE',
        'RACE3_CODE',
        'RACE4_CODE',
        'RACE5_CODE',
        'RACE_ETHNIC_CODE',
        'SOCIAL_SECURITY_NUMBER',
        'STATE_STUDENT_ID',
        'SUFFIX',
      ];
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadCertificateTypes',
      'loadEthnicities',
      'loadGenders',
      'loadGrades',
      'loadHighestDegrees',
      'loadRaces',
      'loadResidencyStatuses',
      'loadStates',
      'loadSubjectMatterAreas',
    ]),
    anyFieldsEnabled(fieldNames) {
      // check whether any field is enabled
      const items = fieldNames.filter(fn => this.fieldEnabled(fn));
      return items && items.length > 0;
    },
    async handleFallbackRequest(recordId) {
      try {
        // Determine the correct API endpoint
        const endpoint = this.isRetired
          ? `retired-person/${this.$route.params.id}/location`
          : `person/${this.$route.params.id}/location`;

        // Perform the fallback request
        const { data } = await axios.get(endpoint);

        // Filter the fallback data based on lastTranSerialNumber
        const filteredData = data
          .filter(item => String(item.lastTranSerialNumber) === String(recordId));
        const filteredObject = filteredData.length > 0 ? filteredData[0] : {};
        // Push only the filtered data to recordLocationHistory
        this.recordLocationHistory.push(filteredObject);
      } catch (fallbackError) {
        console.error('Fallback request failed:', fallbackError);
      }
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        const { id } = this.$route.params;
        const { recordId } = this.$route.query;
        const baseUrl = this.isRetired ? `retired-person/${id}` : `person/${id}`;

        // Fetch record history
        const { data: recordData } = await axios.get(`${baseUrl}/history/${recordId}`);
        this.record = recordData;

        try {
          // Fetch location history
          const { data: locationData } = await axios.get(`${baseUrl}/locationhistory/${recordId}`);
          locationData.districtName = locationData.district.location.name;
          locationData.schoolName = locationData.school.location.name;
          this.recordLocationHistory.push(locationData);
        } catch (error) {
          if (error.response && error.response.status !== 200) {
            await this.handleFallbackRequest(recordId);
          } else {
            console.error('Request failed:', error);
          }
        }

        // Additional API calls for non-retired persons
        if (!this.isRetired) {
          const [{ data: transactionData }, { data: transactionBatchData }] = await Promise.all([
            axios.get(`personid/transactions/details?pidSubmissionId=${recordId}&getDetails=true`),
            axios.get(`/personid/transactions/${recordId}/transbatch`),
          ]);

          [this.personMatchingInformation] = transactionData.data;
          this.personTransactionInformation = transactionBatchData;
        }
      } catch (e) {
        console.log(e);
        this.loadingError = true;
        this.showNotification('danger', [e.response.data.message]);
      } finally {
        this.loading = false;
      }
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.record.personType);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.record.personType);
    },
    mapCertificateType(code) {
      return this.getDescriptionFromCode(code, this.activeCertificateTypes, code);
    },
    mapEthnicSubGroup(code) {
      return this.getDescriptionFromCode(code, this.activeEthnicSubGroups, code);
    },
    mapHighestDegreeEarned(code) {
      return this.getDescriptionFromCode(code, this.activeHighestDegrees, code);
    },
    mapRace(race) {
      return this.getDescriptionFromCode(race, this.activeRaces, race);
    },
    mapResidencyStatus(code) {
      return this.getDescriptionFromCode(code, this.activeResidencyStatuses, code);
    },
    mapSubjectMatterArea(code) {
      return this.getDescriptionFromCode(code, this.activeSubjectMatterAreas, code);
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return null;
    },
  },
};
</script>

<style lang="sass" scoped>
.icon-right
  padding-right: 1.5rem
  padding-top: 1.5rem
.info-columns
  display: flex
  flex-wrap: wrap
  gap: 1rem
  margin-top: 1rem
.info-columns > *
  flex-grow: 1
  min-width: 0
.margin--top
  margin-top: 1rem
.content
  padding: 1rem 0
.columns
  display: flex
  flex-wrap: wrap
  & .column
      flex: 1 0 auto
.field.is-horizontal
  display: flex
</style>
